import { AgreementTypeEnum, CharterSubtype, CharterType } from "__generated__/enums";
import { AgreementType } from "view/agreementtypes/types/AgreementType";
import { Button } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";
import { getCompatibleAgreementsByCharterType_query } from "view/agreements/utils/AgreementQueries";
import { buildAgreementOptions } from "view/agreements/utils/AgreementUtils";
import AgreementSelectPopover from "./AgreementSelectPopover";
import { useRef, useState, useCallback, useEffect } from "react";

type AddAgreementButtonProps = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	agreementTypes?: AgreementType[];
	customAgreementTypes?: AgreementTypeEnum[];
	append: (type: AgreementTypeEnum) => void;
	isParentContract?: boolean;
	isChildContract?: boolean;
}

const AddAgreementButton = ({ append, charterType, charterSubtype, agreementTypes, customAgreementTypes, isParentContract, isChildContract }: AddAgreementButtonProps) => {
	const favorites = useStoreState(state => state.session.user?.properties.favorites) ?? [];

	const { data } = useQuery(getCompatibleAgreementsByCharterType_query, {
		variables: {
			charterType: charterType,
			charterSubtype: charterSubtype,
			isParentContract: isParentContract,
			isChildContract: isChildContract,
		},
	});

	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const open = Boolean(anchorEl);

	const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	}, []);

	const handleClose = useCallback(() => {
		setAnchorEl(null);
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current &&
				event.target instanceof Node &&
				!containerRef.current.contains(event.target)) {
				handleClose();
			}
		};
		if (open) {
			document.addEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [ open, handleClose ]);

	const compatibleAgreements = data?.compatibleAgreementsByCharterType ?? [];
	const options = buildAgreementOptions({ compatibleAgreements, agreementTypes, favorites, customAgreementTypes });

	return (
		<div ref={containerRef}>
			<Button
				onClick={handleOpen}
				style={{ marginLeft: 4 }}
				variant="text"
				size="small">
				{intl.formatMessage({ id: "ADD_BUTTON" })}
			</Button>

			<AgreementSelectPopover
				open={open}
				anchorEl={anchorEl}
				options={options}
				agreementTypes={agreementTypes}
				onSelect={append}
				onClose={handleClose}
			/>
		</div>
	);
};

export default AddAgreementButton;
